"use client";

import { useEffect, useRef } from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import type { Transaction } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-text-style/color";
import { FontFamily } from "@tiptap/extension-font-family";
import { TableKit } from "@tiptap/extension-table";
import { Placeholder } from "@tiptap/extensions";

import { cn } from "@/lib/utils";
import { Locale, getDir } from "@/lib/localization";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { TextDirection } from "../../../lib/textDirection";
import { LabelParagraph } from "../../../lib/textLabel";
import { FontWeight } from "../../../lib/fontWeight";
import { FontCleanup } from "../../../lib/fontCleanup";
import { DeleteEmptyParagraph } from "../../../lib/deleteEmptyParagraph";
import { KeepHeadingOnEmpty } from "../../../lib/keepHeadingOnEmpty";
import { ListStyle } from "../../../lib/listStyle";
import { trackPendingEdit } from "../../../hooks/useUndoRedo";
import {
    GROUP_SEED_META,
    registerGroupEditor,
    seedEmptyFromGroup,
    unregisterGroupEditor,
    type BlockTag,
} from "../../../lib/groupFormat";
// Copied from lomda V2 so the toolbar's colour picker keeps the selected text
// highlighted while the popover is open (editor has lost focus) — same UX as V2.
import "./richText.css";

interface RichTextProps {
    /** Initial content — the editor is uncontrolled after mount. */
    initialHtml: string;
    placeholder?: string;
    editable?: boolean;
    autoFocus?: boolean;
    editorClassName?: string;
    /** Called on blur / unmount — NOT on every keystroke. */
    onCommit: (html: string) => void;
    /** Report this region's editor up so the block can show one shared toolbar. */
    onFocusEditor?: (editor: Editor) => void;
    /** Drive the editor's main contextual topbar via the store (main editor blocks). */
    useGlobalToolbar?: boolean;
    /** Owning block id — lets block-level topbar controls (e.g. Width) target it. */
    blockId?: string;
    /** Center the placeholder (for centered editors like the quote block). */
    centerPlaceholder?: boolean;
    /** Formatting-group key (e.g. `${blockId}:step-title`). Editors that share a
     *  key share one tag: changing one field's tag applies it to all of them. */
    groupKey?: string;
    /** Type italic by default while the field is empty (the quote body is italic
     *  in Figma). A real mark, not CSS — so the toolbar shows italic selected and
     *  the user can still turn it off. */
    defaultItalic?: boolean;
    /** Disable TipTap's markdown input/paste rules (e.g. `_word_` → italic,
     *  `1.` → ordered list). The complete-sentence block marks blanks as literal
     *  `_word_`, so the underscores must survive typing instead of turning the
     *  word italic. */
    disableInputRules?: boolean;
    /** The tag a select-all + type/Delete leaves in a multi-line field — the
     *  same tag the field is seeded with when empty. */
    defaultTag?: BlockTag;
}

// Drop trailing empty paragraphs so a block doesn't keep an undeletable blank
// line at the end. In a heading-default field a lone `<p></p>` is the author's
// emptied Paragraph line, not "no content" — saving "" would make the block
// re-seed the default tag.
const serializeHtml = (html: string, defaultTag: BlockTag) =>
    html.replace(/(?:<p>(?:\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/gi, "") ||
    (defaultTag === "paragraph" ? "" : "<p></p>");

// Uncontrolled TipTap editor: keystrokes stay local and only flush to the store
// on blur or unmount, so typing never re-renders the whole editor state.
export default function RichText({
    initialHtml,
    placeholder,
    editable = true,
    autoFocus,
    editorClassName,
    onCommit,
    onFocusEditor,
    useGlobalToolbar,
    blockId,
    centerPlaceholder,
    groupKey,
    defaultItalic,
    disableInputRules,
    defaultTag = "paragraph",
}: RichTextProps) {
    const setActiveEditor = useLomdaV3Editor((s) => s.setActiveEditor);
    const releaseActiveEditor = useLomdaV3Editor((s) => s.releaseActiveEditor);
    const language = useLomdaV3Editor((s) => s.metadata.language);
    const committedRef = useRef(initialHtml);
    const groupSeedRef = useRef<string | null>(null);
    const commitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const dir = getDir(language as Locale);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                paragraph: false,
                heading: { levels: [1, 2, 3, 4, 5, 6] },
                link: { openOnClick: false, autolink: true },
                // Don't auto-append an empty paragraph after lists/blocks — it
                // showed an undeletable trailing line in edit mode that wasn't
                // in the saved content.
                trailingNode: false,
            }),
            LabelParagraph,
            KeepHeadingOnEmpty.configure({ defaultTag, groupKey }),
            ListStyle,
            TextAlign.configure({ types: ["heading", "paragraph"] }),
            TextStyle,
            Color,
            FontFamily,
            FontWeight,
            FontCleanup,
            TextDirection,
            TableKit.configure({ table: { resizable: false } }),
            // Node-native placeholder: renders inside the empty node, so it
            // inherits that node's tag size / weight / line-height / alignment.
            // showOnlyCurrent:false → an empty field shows its placeholder even
            // when it isn't focused (matches the old always-on placeholder).
            Placeholder.configure({
                placeholder: placeholder ?? "",
                showOnlyCurrent: false,
            }),
        ],
        content: initialHtml || undefined,
        editable,
        immediatelyRender: false,
        // The complete-sentence editor keeps `_word_` literal (see prop docs).
        enableInputRules: !disableInputRules,
        enablePasteRules: !disableInputRules,
        editorProps: {
            attributes: {
                class: cn(
                    "lomda-v3-rich-text",
                    editorClassName,
                    centerPlaceholder && "lomda-v3-ph-center"
                ),
                dir,
            },
        },
        onUpdate: () => {
            // Flush edits that don't end in a blur — e.g. a colour/format change
            // applied from a toolbar popover while the editor is blurred — so
            // they reach the store and become proper undo steps.
            if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
            commitTimerRef.current = setTimeout(() => {
                commitTimerRef.current = null;
                commitRef.current();
            }, 350);
        },
    });

    useEffect(
        () =>
            trackPendingEdit(() => {
                if (!commitTimerRef.current) return;
                clearTimeout(commitTimerRef.current);
                commitTimerRef.current = null;
                commitRef.current();
            }),
        []
    );

    const commitRef = useRef<() => void>(() => {});
    commitRef.current = () => {
        if (!editor) return;
        const html = serializeHtml(editor.getHTML(), defaultTag);
        if (html !== committedRef.current && html !== groupSeedRef.current) {
            committedRef.current = html;
            groupSeedRef.current = null;
            onCommit(html);
        }
    };

    useEffect(() => {
        if (!editor) return;
        const onBlur = () => {
            if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
            commitTimerRef.current = null;
            commitRef.current();
        };
        const onFocus = () => {
            if (useGlobalToolbar) setActiveEditor(editor, blockId, groupKey);
            else onFocusEditor?.(editor);
        };
        editor.on("blur", onBlur);
        editor.on("focus", onFocus);
        return () => {
            editor.off("blur", onBlur);
            editor.off("focus", onFocus);
        };
    }, [
        editor,
        onFocusEditor,
        useGlobalToolbar,
        setActiveEditor,
        blockId,
        groupKey,
    ]);

    // A quiet group-tag seed is not an edit — don't save it on blur.
    useEffect(() => {
        if (!editor) return;
        const onTransaction = ({
            transaction,
        }: {
            transaction: Transaction;
        }) => {
            if (transaction.getMeta(GROUP_SEED_META))
                groupSeedRef.current = serializeHtml(
                    editor.getHTML(),
                    defaultTag
                );
            else if (transaction.docChanged) groupSeedRef.current = null;
        };
        editor.on("transaction", onTransaction);
        return () => {
            editor.off("transaction", onTransaction);
        };
    }, [editor, defaultTag]);

    // Register in the formatting group so a tag change on one field can be
    // mirrored to every sibling field that shares this group key.
    useEffect(() => {
        if (!editor || !groupKey || !editable) return;
        registerGroupEditor(groupKey, editor);
        seedEmptyFromGroup(groupKey, editor);
        return () => unregisterGroupEditor(groupKey, editor);
    }, [editor, groupKey, editable]);

    useEffect(() => {
        return () => {
            if (commitTimerRef.current) clearTimeout(commitTimerRef.current);
            commitRef.current();
            if (useGlobalToolbar && editor) releaseActiveEditor(editor);
        };
    }, [editor, useGlobalToolbar, releaseActiveEditor]);

    // Italic-by-default: seeding "<em></em>" can't work (an empty mark has no
    // text to attach to, so TipTap drops it). Instead arm italic as a stored
    // mark while the field is empty, so the first keystroke is italic and the
    // toolbar reads it as selected. Only while empty — never fights the user
    // once they've typed.
    useEffect(() => {
        if (!editor || !defaultItalic || !editable) return;
        const arm = () => {
            if (editor.isEmpty && !editor.isActive("italic")) {
                editor.chain().setItalic().run();
            }
        };
        arm();
        editor.on("focus", arm);
        return () => {
            editor.off("focus", arm);
        };
    }, [editor, defaultItalic, editable]);

    useEffect(() => {
        editor?.setEditable(editable);
    }, [editor, editable]);

    // Sync store → editor for EXTERNAL changes (undo / redo / programmatic).
    // Our own edits set committedRef on commit, so normal typing is a no-op here
    // and never resets the caret; only a value that differs from what we last
    // committed (e.g. an undo) re-applies content — and never autofocuses, so an
    // undo that empties the selected text can't pull the next redo into it.
    useEffect(() => {
        if (!editor) return;
        const incoming = initialHtml || "";
        if (incoming !== (committedRef.current || "")) {
            const current = editor.getHTML();
            const normalizedCurrent = current === "<p></p>" ? "" : current;
            if (incoming === normalizedCurrent) return;
            committedRef.current = incoming;
            editor.commands.setContent(incoming, { emitUpdate: false });
            return;
        }
        // Scrolling a new block in is `useRevealBlock`'s job; the editor's own
        // instant scroll would cut that smooth scroll short.
        if (autoFocus) editor.commands.focus("end", { scrollIntoView: false });
    }, [editor, initialHtml, autoFocus]);

    if (!editor) return null;

    return (
        <div
            className="relative"
            onPointerDown={() => {
                if (useGlobalToolbar && editable) {
                    setActiveEditor(editor, blockId, groupKey);
                }
            }}
        >
            <EditorContent editor={editor} />
        </div>
    );
}
